export type PlanStatus = 'failed' | 'passed' | 'skipped' | 'question';
export type PlanPriority = 'p0' | 'p1' | 'p2' | 'p3';
export type PlanItem = {
  name: string;
  status: PlanStatus;
  details?: string;
  priority?: PlanPriority;
  /** Case asserts correct rejection; mirrors ExploratoryTestPlanItem in features/exploratory-tests/types.ts. */
  negative?: boolean;
  /** Expected behavior was unknowable up front; mirrors ExploratoryTestPlanItem. */
  ambiguous?: boolean;
};

/**
 * P0-driven verdict for the header chip. p0 outcomes drive pass/fail/blocked;
 * lower-priority outcomes are surfaced inline in the test plan list, not here.
 */
export function deriveHeaderVerdict(
  testPlan: ReadonlyArray<PlanItem>,
  appended: ReadonlyArray<PlanItem>
): 'pass' | 'fail' | 'blocked' | null {
  const all = [...testPlan, ...appended];
  if (all.length === 0) return null;
  const p0 = all.filter((i) => (i.priority ?? 'p1') === 'p0');
  if (p0.length === 0) {
    // Mirrors computeExploratoryVerdict's fallback: questions never block here —
    // the tester exercised those flows — only skips with nothing passed do.
    if (all.some((i) => i.status === 'failed')) return 'fail';
    if (all.some((i) => i.status === 'skipped') && !all.some((i) => i.status === 'passed')) {
      return 'blocked';
    }
    return 'pass';
  }
  if (p0.some((i) => i.status === 'failed')) return 'fail';
  if (p0.some((i) => i.status === 'skipped' || i.status === 'question')) return 'blocked';
  return 'pass';
}

export const HEADER_VERDICT_META: Record<
  'pass' | 'fail' | 'blocked',
  { label: string; color: 'green' | 'red' | 'yellow' }
> = {
  pass: { label: 'Pass', color: 'green' },
  fail: { label: 'Fail', color: 'red' },
  blocked: { label: 'Blocked', color: 'yellow' },
};
